import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  MenuToggle,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr, TreeRowWrapper } from '@patternfly/react-table';
import { ExclamationCircleIcon, NetworkIcon, SearchIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useNetworks } from '../../../hooks/useNetworks';
import { type NetworkTreeRow, buildAncestorIndex, buildNetworkTree, defaultExpandedIds } from './networkTree';
import { renderDescription, renderFamilyLabel, renderStatusLabel, renderVlan } from './networkLabels';

type FamilyFilter = 'all' | '4' | '6';

const familyFilterLabel: Record<FamilyFilter, string> = {
  all: 'All families',
  '4': 'IPv4',
  '6': 'IPv6',
};

function NetworksTab() {
  const { networks, loading, error, refetch } = useNetworks();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedFamily, setSelectedFamily] = useState<FamilyFilter>('all');
  const [isFamilySelectOpen, setIsFamilySelectOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Tree state. `expandedIds` is local-only (never persisted across reloads).
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set<number>());

  // Snapshot of `expandedIds` taken when the user first activates a filter,
  // restored when they clear back to "no filters" so their manual expansions
  // survive a search-and-clear cycle.
  const preFilterExpansionRef = useRef<Set<number> | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [selectedFamily]);

  // 1. Build the tree once per fresh `networks` payload.
  const treeRows = useMemo<NetworkTreeRow[]>(() => buildNetworkTree(networks), [networks]);

  // 2. Reset expansion to "all roots open" whenever the tree changes.
  useEffect(() => {
    setExpandedIds(defaultExpandedIds(treeRows));
    preFilterExpansionRef.current = null;
  }, [treeRows]);

  // 3. Index: row.id -> ancestor ids (root → immediate parent). Used both
  //    to compute `isHidden` per row and to force-expand ancestors of search
  //    matches when a filter is active.
  const ancestorIndex = useMemo(() => buildAncestorIndex(treeRows), [treeRows]);

  const filtersActive = debouncedSearch.trim() !== '' || selectedFamily !== 'all';

  // 4. Apply search / family filter and expand ancestors of every match. While
  //    a filter is active, we shadow the user's `expandedIds` with a
  //    force-expand set so all surviving ancestors are visible.
  const { visibleRows, effectiveExpandedIds } = useMemo(() => {
    if (treeRows.length === 0) {
      return {
        visibleRows: [] as NetworkTreeRow[],
        effectiveExpandedIds: expandedIds,
      };
    }

    if (!filtersActive) {
      return {
        visibleRows: treeRows,
        effectiveExpandedIds: expandedIds,
      };
    }

    const q = debouncedSearch.trim().toLowerCase();
    const matchIds = new Set<number>();
    for (const r of treeRows) {
      const n = r.network;
      if (selectedFamily !== 'all' && String(n.family) !== selectedFamily) {
        continue;
      }
      if (q) {
        const prefixMatch = n.prefix.toLowerCase().includes(q);
        const descMatch = n.description.toLowerCase().includes(q);
        const vlanNameMatch = n.vlan?.name.toLowerCase().includes(q) ?? false;
        const vlanVidMatch = n.vlan ? String(n.vlan.vid).includes(q) : false;
        if (!prefixMatch && !descMatch && !vlanNameMatch && !vlanVidMatch) {
          continue;
        }
      }
      matchIds.add(n.id);
    }

    // Visible = matches ∪ ancestors(matches).
    const surviving = new Set<number>(Array.from(matchIds));
    const forced = new Set<number>(Array.from(expandedIds));
    // Walk matches once, expanding ancestors into both the surviving and
    // force-expand sets (the two used to be separate loops; consolidated for
    // clarity and to avoid iterating a Set twice).
    Array.from(matchIds).forEach((id) => {
      const chain = ancestorIndex.get(id) ?? [];
      for (const ancestorId of chain) {
        surviving.add(ancestorId);
        forced.add(ancestorId);
      }
    });

    // Walk treeRows in their canonical order, keeping only surviving rows.
    const filteredRows: NetworkTreeRow[] = [];
    for (const r of treeRows) {
      if (surviving.has(r.network.id)) {
        filteredRows.push(r);
      }
    }

    return {
      visibleRows: filteredRows,
      effectiveExpandedIds: forced,
    };
  }, [treeRows, filtersActive, debouncedSearch, selectedFamily, expandedIds, ancestorIndex]);

  // 5. Per-row visibility. A row is hidden when any of its ancestors is
  //    collapsed in `effectiveExpandedIds`.
  const renderedRows = useMemo(() => {
    return visibleRows.map((row) => {
      const ancestors = ancestorIndex.get(row.network.id) ?? [];
      const isHidden = ancestors.some((id) => !effectiveExpandedIds.has(id));
      const isExpanded = effectiveExpandedIds.has(row.network.id) && row.network.children > 0;
      return { row, isHidden, isExpanded };
    });
  }, [visibleRows, ancestorIndex, effectiveExpandedIds]);

  // 6. Pagination operates on visible (not hidden) rows in tree order.
  const visibleNonHiddenRows = useMemo(() => renderedRows.filter((r) => !r.isHidden), [renderedRows]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return visibleNonHiddenRows.slice(start, start + perPage);
  }, [visibleNonHiddenRows, page, perPage]);

  // Reset to page 1 whenever the visible-rows count changes (search, filter,
  // or expansion change can each affect the count).
  useEffect(() => {
    setPage(1);
  }, [visibleNonHiddenRows.length]);

  // Snapshot/restore the user's expansion across a filter-active cycle.
  useEffect(() => {
    if (filtersActive) {
      if (preFilterExpansionRef.current === null) {
        preFilterExpansionRef.current = new Set(expandedIds);
      }
    } else if (preFilterExpansionRef.current !== null) {
      setExpandedIds(preFilterExpansionRef.current);
      preFilterExpansionRef.current = null;
    }
    // We intentionally exclude `expandedIds` from deps — we only want to
    // snapshot at the moment filters flip on, not on every expand toggle
    // while filters are already active.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersActive]);

  const toggleExpanded = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const onClearFilters = useCallback(() => {
    setSearchValue('');
    setDebouncedSearch('');
    setSelectedFamily('all');
    if (preFilterExpansionRef.current !== null) {
      setExpandedIds(preFilterExpansionRef.current);
      preFilterExpansionRef.current = null;
    }
  }, []);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const onFamilySelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    if (value === 'all' || value === '4' || value === '6') {
      setSelectedFamily(value);
    }
    setIsFamilySelectOpen(false);
  };

  if (loading) {
    return <CenteredSpinner />;
  }

  if (error) {
    return (
      <EmptyState
        titleText="Unable to load networks"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={() => refetch()}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  if (networks.length === 0) {
    return (
      <EmptyState titleText="No networks found" icon={NetworkIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>No networks are currently assigned to your organization.</EmptyStateBody>
      </EmptyState>
    );
  }

  const itemCount = visibleNonHiddenRows.length;

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by prefix, description, or VLAN..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              isOpen={isFamilySelectOpen}
              selected={selectedFamily}
              onSelect={onFamilySelect}
              onOpenChange={(isOpen) => setIsFamilySelectOpen(isOpen)}
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsFamilySelectOpen(!isFamilySelectOpen)}
                  isExpanded={isFamilySelectOpen}
                  aria-label="Filter by IP family"
                >
                  {familyFilterLabel[selectedFamily]}
                </MenuToggle>
              )}
            >
              <SelectList>
                <SelectOption value="all">All families</SelectOption>
                <SelectOption value="4">IPv4</SelectOption>
                <SelectOption value="6">IPv6</SelectOption>
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Button variant="link" isInline isDisabled={!filtersActive} onClick={onClearFilters}>
              Clear filters
            </Button>
          </ToolbarItem>
          <ToolbarItem variant="pagination">
            <Pagination
              itemCount={itemCount}
              perPage={perPage}
              page={page}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {itemCount === 0 ? (
        <EmptyState titleText="No networks match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Try a different search term or family filter, or clear the filters to see all networks.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={onClearFilters}>
                Clear filters
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : (
        <Table isTreeTable aria-label="Networks list" variant="compact">
          <Thead>
            <Tr>
              <Th>Prefix</Th>
              <Th>Family</Th>
              <Th>VLAN</Th>
              <Th>Status</Th>
              <Th>Description</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedRows.map(({ row, isHidden, isExpanded }, idx) => {
              const network = row.network;
              const treeRowProps = {
                isExpanded,
                isHidden,
                'aria-level': row.effectiveDepth + 1,
                'aria-posinset': row.posinset,
                // PatternFly's tree decorator renders the expand/collapse
                // toggle only when this is > 0, so we pass the child count
                // (not sibling count) — leaf rows have no toggle.
                'aria-setsize': network.children,
                toggleAriaLabel: `Toggle ${network.prefix}`,
              };
              return (
                <TreeRowWrapper key={network.id} row={{ props: treeRowProps }}>
                  <Td
                    dataLabel="Prefix"
                    treeRow={{
                      onCollapse: () => toggleExpanded(network.id),
                      props: treeRowProps,
                      rowIndex: idx,
                    }}
                  >
                    <Link to={`/datacenter/networks/${network.id}`}>{network.prefix}</Link>
                  </Td>
                  <Td dataLabel="Family">{renderFamilyLabel(network.family)}</Td>
                  <Td dataLabel="VLAN">{renderVlan(network.vlan)}</Td>
                  <Td dataLabel="Status">{renderStatusLabel(network.status)}</Td>
                  <Td dataLabel="Description">{renderDescription(network.description)}</Td>
                  <Td dataLabel="Actions">
                    {network.children === 0 ? (
                      <Button
                        variant="link"
                        isInline
                        component={(props) => <Link {...props} to={`/datacenter/networks/${network.id}#ips`} />}
                      >
                        View addresses
                      </Button>
                    ) : null}
                  </Td>
                </TreeRowWrapper>
              );
            })}
          </Tbody>
        </Table>
      )}

      {itemCount > 0 && (
        <Pagination
          itemCount={itemCount}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      )}
    </>
  );
}

export default NetworksTab;
